import { logger } from "@/lib/logger";
import { typedSupabase } from "@/lib/typed-supabase";
import { registerService } from '../core/service-registry';
import type {

  IService,
  ServiceResponse,
  StorageBucketConfig,
  FileUploadResult,
} from "@flowstate/core";

/**
 * Unified Storage Service
 * Polymorphic abstraction for Supabase Storage operations across all buckets
 * Follows architectural consolidation patterns from Phase 2
 * Types imported from @flowstate/core for consistency
 */

// ==================== Additional Local Types ====================
// These remain local as they use bucket-specific generics

export interface UploadFileRequest {
  bucket: keyof typeof STORAGE_BUCKETS;
  path: string;
  file: File | Blob;
  options?: {
    cacheControl?: string;
    contentType?: string;
    upsert?: boolean;
  };
}

export interface DeleteFileRequest {
  bucket: keyof typeof STORAGE_BUCKETS;
  path: string;
}

export interface GetUrlRequest {
  bucket: keyof typeof STORAGE_BUCKETS;
  path: string;
  download?: boolean;
}

// ==================== Constants ====================

// ==================== Constants ====================

export const STORAGE_BUCKETS = {
  // Task attachments - comprehensive file support
  taskAttachments: {
    name: "task-attachments",
    maxSize: 10 * 1024 * 1024, // 10MB
    isPublic: false,
    allowedMimeTypes: [
      "image/*",
      "application/pdf",
      "text/*",
      "application/json",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.*",
    ],
  },

  // Season badges - public images only
  seasonBadges: {
    name: "public",
    maxSize: 2 * 1024 * 1024, // 2MB
    isPublic: true,
    allowedMimeTypes: ["image/*"],
  },

  // Theme background images
  backgroundImages: {
    name: "background-images",
    maxSize: 5 * 1024 * 1024, // 5MB
    isPublic: true,
    allowedMimeTypes: ["image/*"],
  },

  // Community post images
  postImages: {
    name: "post-images",
    maxSize: 5 * 1024 * 1024, // 5MB
    isPublic: true,
    allowedMimeTypes: ["image/*"],
  },

  // User avatars
  avatars: {
    name: "profile-images",
    maxSize: 2 * 1024 * 1024, // 2MB
    isPublic: true,
    allowedMimeTypes: ["image/*"],
  },

  // Documentation files
  docs: {
    name: "docs",
    maxSize: 50 * 1024 * 1024, // 50MB
    isPublic: false,
    allowedMimeTypes: [
      "text/markdown",
      "application/pdf",
      "text/plain",
      "application/json",
    ],
  },
} as const;

// ==================== Service Implementation ====================

export class StorageService implements IService {
  readonly name = "storage";

  async initialize(): Promise<void> {
    logger.info("📦 StorageService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("📦 StorageService disposed");
  }

  /**
   * Upload a file to a specified bucket
   * Validates file size and type before upload
   */
  async uploadFile(
    request: UploadFileRequest
  ): Promise<ServiceResponse<FileUploadResult>> {
    const { bucket, path, file, options = {} } = request;

    try {
      // Get bucket config
      const bucketConfig = STORAGE_BUCKETS[bucket];
      if (!bucketConfig) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`,
        };
      }

      // Validate file size
      if (file.size > bucketConfig.maxSize) {
        const maxSizeMB = Math.round(bucketConfig.maxSize / 1024 / 1024);
        return {
          success: false,
          error: `File size exceeds ${maxSizeMB}MB limit`,
        };
      }

      // Validate file type
      const fileType = file.type || "application/octet-stream";
      if (bucketConfig.allowedMimeTypes) {
        const isAllowed = bucketConfig.allowedMimeTypes.some((pattern) => {
          if (pattern.endsWith("*")) {
            return fileType.startsWith(pattern.slice(0, -1));
          }
          return fileType === pattern;
        });

        if (!isAllowed) {
          return {
            success: false,
            error: `File type ${fileType} not allowed for this bucket`,
          };
        }
      }

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } =
        await typedSupabase.storage
          .from(bucketConfig.name)
          .upload(path, file, {
            cacheControl: options.cacheControl ?? "3600",
            contentType: options.contentType ?? fileType,
            upsert: options.upsert ?? false,
          });

      if (uploadError) {
        return {
          success: false,
          error: `Upload failed: ${uploadError.message}`,
        };
      }

      // Get public URL
      const publicUrl = this.getPublicUrlSync(bucket, uploadData.path);

      return {
        success: true,
        data: {
          path: uploadData.path,
          publicUrl,
          bucket: bucketConfig.name,
          size: file.size,
          contentType: fileType,
        },
      };
    } catch (error) {
      logger.error("StorageService.uploadFile error:", { error: error });
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown upload error",
      };
    }
  }

  /**
   * Delete a file from storage
   */
  async deleteFile(
    request: DeleteFileRequest
  ): Promise<ServiceResponse<void>> {
    const { bucket, path } = request;

    try {
      const bucketConfig = STORAGE_BUCKETS[bucket];
      if (!bucketConfig) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`,
        };
      }

      const { error } = await typedSupabase.storage
        .from(bucketConfig.name)
        .remove([path]);

      if (error) {
        return {
          success: false,
          error: `Delete failed: ${error.message}`,
        };
      }

      return { success: true, data: undefined };
    } catch (error) {
      logger.error("StorageService.deleteFile error:", { error: error });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown delete error",
      };
    }
  }

  /**
   * Get public URL for a file (synchronous - no API call)
   */
  getPublicUrlSync(
    bucket: keyof typeof STORAGE_BUCKETS,
    path: string
  ): string {
    const bucketConfig = STORAGE_BUCKETS[bucket];
    if (!bucketConfig) {
      throw new Error(`Invalid bucket: ${bucket}`);
    }

    const { data } = typedSupabase.storage
      .from(bucketConfig.name)
      .getPublicUrl(path);

    return data.publicUrl;
  }

  /**
   * Get signed URL for private files
   */
  async getSignedUrl(
    request: GetUrlRequest & { expiresIn?: number }
  ): Promise<ServiceResponse<string>> {
    const { bucket, path, expiresIn = 3600 } = request;

    try {
      const bucketConfig = STORAGE_BUCKETS[bucket];
      if (!bucketConfig) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`,
        };
      }

      const { data, error } = await typedSupabase.storage
        .from(bucketConfig.name)
        .createSignedUrl(path, expiresIn);

      if (error || !data) {
        return {
          success: false,
          error: `Failed to create signed URL: ${error?.message}`,
        };
      }

      return {
        success: true,
        data: data.signedUrl,
      };
    } catch (error) {
      logger.error("StorageService.getSignedUrl error:", { error: error });
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown signed URL error",
      };
    }
  }

  /**
   * Download a file as a Blob
   */
  async downloadFile(
    request: GetUrlRequest
  ): Promise<ServiceResponse<Blob>> {
    const { bucket, path } = request;

    try {
      const bucketConfig = STORAGE_BUCKETS[bucket];
      if (!bucketConfig) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`,
        };
      }

      const { data, error } = await typedSupabase.storage
        .from(bucketConfig.name)
        .download(path);

      if (error || !data) {
        return {
          success: false,
          error: `Download failed: ${error?.message}`,
        };
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      logger.error("StorageService.downloadFile error:", { error: error });
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown download error",
      };
    }
  }

  /**
   * List files in a bucket path
   */
  async listFiles(
    bucket: keyof typeof STORAGE_BUCKETS,
    path?: string,
    options?: {
      limit?: number;
      offset?: number;
      sortBy?: { column: string; order: "asc" | "desc" };
    }
  ): Promise<ServiceResponse<Array<{ name: string; id: string; metadata: Record<string, unknown> }>>> {
    try {
      const bucketConfig = STORAGE_BUCKETS[bucket];
      if (!bucketConfig) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`,
        };
      }

      const { data, error } = await typedSupabase.storage
        .from(bucketConfig.name)
        .list(path, options);

      if (error) {
        return {
          success: false,
          error: `List failed: ${error.message}`,
        };
      }

      return {
        success: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: (data || []) as any,
      };
    } catch (error) {
      logger.error("StorageService.listFiles error:", { error: error });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown list error",
      };
    }
  }

  /**
   * Move/rename a file within the same bucket
   */
  async moveFile(
    bucket: keyof typeof STORAGE_BUCKETS,
    fromPath: string,
    toPath: string
  ): Promise<ServiceResponse<void>> {
    try {
      const bucketConfig = STORAGE_BUCKETS[bucket];
      if (!bucketConfig) {
        return {
          success: false,
          error: `Invalid bucket: ${bucket}`,
        };
      }

      const { error } = await typedSupabase.storage
        .from(bucketConfig.name)
        .move(fromPath, toPath);

      if (error) {
        return {
          success: false,
          error: `Move failed: ${error.message}`,
        };
      }

      return { success: true, data: undefined };
    } catch (error) {
      logger.error("StorageService.moveFile error:", { error: error });
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown move error",
      };
    }
  }

  /**
   * Get bucket configuration
   */
  getBucketConfig(bucket: keyof typeof STORAGE_BUCKETS): StorageBucketConfig {
    return STORAGE_BUCKETS[bucket];
  }

  /**
   * Validate a file before upload (without actually uploading)
   */
  validateFile(
    bucket: keyof typeof STORAGE_BUCKETS,
    file: File | Blob
  ): { valid: boolean; error?: string } {
    const bucketConfig = STORAGE_BUCKETS[bucket];

    // Check size
    if (file.size > bucketConfig.maxSize) {
      const maxSizeMB = Math.round(bucketConfig.maxSize / 1024 / 1024);
      return {
        valid: false,
        error: `File size exceeds ${maxSizeMB}MB limit`,
      };
    }

    // Check type
    const fileType = file.type || "application/octet-stream";
    if (bucketConfig.allowedMimeTypes) {
      const isAllowed = bucketConfig.allowedMimeTypes.some((pattern) => {
        if (pattern.endsWith("*")) {
          return fileType.startsWith(pattern.slice(0, -1));
        }
        return fileType === pattern;
      });

      if (!isAllowed) {
        return {
          valid: false,
          error: `File type ${fileType} not allowed`,
        };
      }
    }

    return { valid: true };
  }
}

// Export singleton instance
export const storageService = new StorageService();

// Register with service registry
registerService("storage", () => storageService);
